import React, { useMemo, useState } from "react";
import { Play, Lock, FileCheck, CircleOff } from "lucide-react";
import { useTranslate } from "../../i18n/useTranslate";
import { usePreferences } from "../../context/PreferencesContext";
import { LiveSection, Stat, Finding, Assumptions } from "./primitives";
import { ASSURANCE_ASSUMPTIONS } from "../../assurance/assurance";
import { privacyPosture, PRIVACY_ASSUMPTIONS } from "../../governance/privacy";
import { buildEvidenceEnvelope, ENVELOPE_FIELDS, EVIDENCE_ASSUMPTIONS } from "../../spine/evidenceEnvelope";
import { runAllMissions, MISSIONS, MISSION_ASSUMPTIONS } from "../../missions/missions";
import { computeProofMetrics, METRIC_IDS, PROOF_ASSUMPTIONS } from "../../missions/proofMetrics";
import { coverageSummary, SECTION_COVERAGE } from "../../architecture/coverage";
import { routerCoverage } from "../../intelligence/router";

const useL = () => {
  const { lang } = usePreferences();
  return (o) => (typeof o === "string" ? o : o?.[lang] ?? o?.en ?? "");
};

/* ── §16 AI stack assurance ────────────────────────────────────────────── */
export function LiveAssuranceSection({ assurance, accuracy }) {
  const t = useTranslate();
  const router = useMemo(() => routerCoverage(), []);

  return (
    <LiveSection
      title="AI Stack Assurance"
      section="§16"
      note={assurance.trustStatement}
    >
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Live data feeds" value={`${assurance.dataAvailability.live}/${assurance.dataAvailability.total}`} tone="red" />
        <Stat label="Sectors observed" value={`${assurance.cameraHealth.sectorsObserved}/${assurance.cameraHealth.sectorsTotal}`} tone="red" />
        <Stat label="Paradigms attached" value={`${router.attached}/${router.paradigmCount}`} tone="amber" />
        <Stat
          label="False-alert rate"
          unknown
          reason={t("No labelled ground truth exists. Any figure here would be fabricated.")}
        />
      </div>

      <div className="mb-3 space-y-1.5">
        {assurance.findings.slice(0, 5).map((f) => (
          <Finding key={f.text} severity={f.severity} area={f.area} text={f.text} />
        ))}
      </div>

      {assurance.latency.measured && (
        <div className="mb-3">
          <div className="mb-1 text-[10px] font-black uppercase tracking-wider text-slate-400">
            {t("Engine latency — genuinely measured")}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {assurance.latency.timings.map((x) => (
              <span
                key={x.label}
                className={`rounded border px-2 py-1 text-[10px] tabular-nums ${
                  x.withinBudget === false
                    ? "border-amber-300 bg-amber-50 text-amber-800"
                    : "border-emerald-200 bg-emerald-50 text-emerald-800"
                }`}
              >
                {x.label} <strong>{x.ms} ms</strong>
                {x.budgetMs && <span className="text-slate-400"> / {x.budgetMs}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-2 md:grid-cols-2">
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3">
          <div className="mb-1 flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-slate-500">
            <CircleOff size={11} /> {t("Model accuracy")}
          </div>
          {assurance.modelHealth.measurable ? (
            <>
              <div className="flex flex-wrap gap-1">
                {assurance.modelHealth.byHorizon.map((h) => (
                  <span key={h.horizon} className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] tabular-nums">
                    +{h.horizon}m <strong>{h.mape}%</strong>
                  </span>
                ))}
              </div>
              <p className="mt-1 text-[10px] leading-snug text-slate-500">{assurance.modelHealth.caveat}</p>
            </>
          ) : (
            <p className="text-[11px] leading-snug text-slate-600">{assurance.modelHealth.reason}</p>
          )}
        </div>
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3">
          <div className="mb-1 flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-slate-500">
            <CircleOff size={11} /> {t("Drift")}
          </div>
          <p className="text-[11px] leading-snug text-slate-600">
            {assurance.drift.measurable
              ? `${assurance.drift.degrading.length} horizon(s) degrading.`
              : assurance.drift.reason}
          </p>
        </div>
      </div>

      <p className="mt-2 rounded-lg border border-slate-300 bg-slate-100 px-3 py-2 text-[11px] leading-snug text-slate-700">
        {assurance.confidence.statement}
      </p>

      <Assumptions items={ASSURANCE_ASSUMPTIONS} />
    </LiveSection>
  );
}

/* ── §15 evidence envelope + §17 privacy posture ───────────────────────── */
export function LiveGovernanceSection() {
  const t = useTranslate();
  const posture = useMemo(() => privacyPosture(), []);

  /* An envelope for an open incident, so the reader sees the honest gaps
   * rather than a tidy completed record. */
  const envelope = useMemo(() => buildEvidenceEnvelope({
    incident: { id: "INC-DEMO", sector: "SEC-01", coord: [73.79, 19.998], detectedAt: Date.now() },
    events: [{ type: "detected", at: Date.now() }],
  }), []);

  return (
    <LiveSection
      title="Decision Provenance & Privacy Posture"
      section="§15 · §17"
      note="An envelope missing a field looks complete to a reader who does not know the field exists. All sixteen are always present; unfillable ones are null with a reason."
    >
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Evidence fields" value={`${envelope.completeness.filled}/${ENVELOPE_FIELDS.length}`} tone="amber" />
        <Stat label="Controls enforced" value={`${posture.enforced}/${posture.total}`} tone="green" />
        <Stat label="Declared only" value={posture.declared} tone="amber" />
        <Stat label="Prohibited categories" value={posture.prohibitedCategories} tone="indigo" />
      </div>

      <div className="mb-3 grid gap-2 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-1.5 flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-slate-400">
            <FileCheck size={11} /> {t("Missing from this record")}
          </div>
          <ul className="space-y-1">
            {envelope.completeness.missing.slice(0, 5).map((m) => (
              <li key={m.field} className="text-[11px] leading-snug text-slate-600">
                <strong className="text-slate-800">{m.field}</strong> — {m.reason}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-1.5 flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-slate-400">
            <Lock size={11} /> {t("Declared, not enforced here")}
          </div>
          <ul className="space-y-1">
            {posture.controls.filter((c) => !c.enforcedHere).map((c) => (
              <li key={c.control} className="text-[11px] leading-snug text-slate-600">
                <strong className="text-slate-800">{c.control}</strong> — {c.owner}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900">
        {envelope.integrity.caveat}
      </p>

      <Assumptions items={[...EVIDENCE_ASSUMPTIONS, ...PRIVACY_ASSUMPTIONS]} />
    </LiveSection>
  );
}

/* ── §21 missions + §22 proof metrics ──────────────────────────────────── */
export function LiveMissionSection({ live, twin, accuracy }) {
  const t = useTranslate();
  const L = useL();
  const [simulateOperator, setSimulateOperator] = useState(false);

  const runs = useMemo(
    () => runAllMissions(live.state, { twin, simulateOperator }),
    [live.state, twin, simulateOperator],
  );
  const proof = useMemo(
    () => computeProofMetrics({
      liveState: live.state, twin, missionRuns: runs, accuracy,
      sessionStartedAt: SESSION_START, simulateOperator,
    }),
    [live.state, twin, runs, accuracy, simulateOperator],
  );
  const cov = useMemo(() => coverageSummary(), []);

  return (
    <LiveSection
      title="Mission Workflows & Proof Metrics"
      section="§21 · §22"
      note={proof.statement}
    >
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setSimulateOperator(!simulateOperator)}
          className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold ${
            simulateOperator
              ? "border-amber-400 bg-amber-100 text-amber-900"
              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
          }`}
        >
          <Play size={12} />
          {simulateOperator ? t("Simulated operator ON") : t("Halt at AUTHORIZE (no human)")}
        </button>
        <p className="text-[10px] leading-snug text-slate-500">
          {simulateOperator
            ? t("Operator-dependent stages are marked simulated and excluded from the measured timings.")
            : t("Every mission stops where a human decision is required. The runner does not perform it.")}
        </p>
      </div>

      <div className="mb-3 space-y-1.5">
        {runs.map((r) => (
          <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-xs font-black text-slate-900">{L(r.label)}</span>
              <span className={`rounded px-1.5 py-0.5 text-[9px] font-black uppercase ${
                r.awaitingAuthorisation ? "bg-amber-100 text-amber-800"
                : r.complete ? "bg-emerald-100 text-emerald-800"
                : "bg-slate-100 text-slate-600"
              }`}>
                {r.awaitingAuthorisation ? t("Awaiting authorisation") : t("Complete")}
              </span>
              <span className="ml-auto text-[10px] tabular-nums text-slate-500">
                {r.measuredMs} ms {t("measured")}
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {r.stages.map((s) => (
                <span
                  key={s.stage}
                  title={`${s.loopStage} · ${s.ms}ms`}
                  className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${
                    s.simulated
                      ? "border border-dashed border-slate-300 bg-slate-50 text-slate-500"
                      : "bg-indigo-100 text-indigo-800"
                  }`}
                >
                  {s.loopStage}
                </span>
              ))}
            </div>
            {r.note && <p className="mt-1 text-[10px] italic leading-snug text-slate-500">{r.note}</p>}
          </div>
        ))}
      </div>

      <div className="mb-2 text-[10px] font-black uppercase tracking-wider text-slate-400">
        {t("The thirteen proof metrics")}
      </div>
      <div className="grid gap-1.5 md:grid-cols-2">
        {METRIC_IDS.map((id) => {
          const m = proof.metrics[id];
          return (
            <div
              key={id}
              className={`rounded-lg border px-2.5 py-1.5 ${
                m.measurable ? "border-emerald-200 bg-emerald-50" : "border-slate-300 bg-slate-50 border-dashed"
              }`}
            >
              <div className="flex items-baseline gap-2">
                <span className="text-[11px] font-bold text-slate-700">{id}</span>
                {m.measurable ? (
                  <span className="ml-auto text-[11px] font-black tabular-nums text-emerald-800">
                    {typeof m.value === "object" ? t("measured") : `${m.value} ${m.unit ?? ""}`}
                  </span>
                ) : (
                  <span className="ml-auto text-[10px] font-black uppercase text-slate-400">
                    {t("not measurable")}
                  </span>
                )}
              </div>
              <p className="text-[10px] leading-snug text-slate-500">
                {m.measurable ? m.note : m.reason}
              </p>
              {!m.measurable && (
                <p className="mt-0.5 text-[10px] leading-snug text-indigo-600">
                  <strong>{t("Would need")}:</strong> {m.whatWouldBeNeeded}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50 p-3">
        <div className="mb-1 text-[10px] font-black uppercase tracking-wider text-indigo-700">
          {t("22-point architecture coverage")}
        </div>
        <div className="mb-1.5 flex flex-wrap gap-1">
          {SECTION_COVERAGE.map((s) => (
            <span
              key={s.n}
              title={`§${s.n} ${s.title} — ${s.status}`}
              className={`rounded px-1.5 py-0.5 text-[9px] font-black tabular-nums ${
                s.status === "built" ? "bg-emerald-200 text-emerald-900"
                : s.status === "partial" ? "bg-amber-200 text-amber-900"
                : "bg-slate-200 text-slate-600"
              }`}
            >
              {s.n}
            </span>
          ))}
        </div>
        <p className="text-[11px] leading-snug text-slate-700">{cov.statement}</p>
      </div>

      <Assumptions items={[...MISSION_ASSUMPTIONS, ...PROOF_ASSUMPTIONS]} />
    </LiveSection>
  );
}

const SESSION_START = Date.now();
